/**
 * Retrieves the marketplace session token, caching it in sessionStorage and sharing
 * the request promise to prevent duplicate concurrent API requests.
 * Automatically handles token expiration.
 */
const FALLBACK_TOKEN_TTL_MS = 9 * 60 * 1000;
const TOKEN_EXPIRY_SKEW_MS = 30 * 1000;

function decodeJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = payloadB64.padEnd(payloadB64.length + ((4 - (payloadB64.length % 4)) % 4), "=");
    return JSON.parse(atob(paddedPayload));
  } catch (_) {
    return null;
  }
}

function getTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  if (payload && Number.isFinite(payload.exp)) {
    return payload.exp * 1000;
  }

  return Date.now() + FALLBACK_TOKEN_TTL_MS;
}

export async function getMarketplaceSessionToken(baseUrl) {
  const now = Date.now();

  // 1. If valid in sessionStorage, return it immediately
  const stored = sessionStorage.getItem("ff_marketplace_session");
  const exp = sessionStorage.getItem("ff_marketplace_session_exp");
  if (stored && exp && now < parseInt(exp, 10)) {
    if (!window.__marketplaceSessionPromise) {
      window.__marketplaceSessionPromise = Promise.resolve(stored);
      window.__marketplaceSessionPromise.exp = parseInt(exp, 10);
    }
    return stored;
  }

  // 2. If it was expired/null and we have an expired promise on window, clear it
  if (window.__marketplaceSessionPromise && window.__marketplaceSessionPromise.exp && now >= window.__marketplaceSessionPromise.exp) {
    window.__marketplaceSessionPromise = null;
  }

  // 3. Start a new fetch if no promise exists
  if (!window.__marketplaceSessionPromise) {
    const promise = (async () => {
      try {
        const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        const res = await fetch(`${cleanBaseUrl}/marketplace/session/bootstrap`, {
          method: "POST"
        });
        const data = await res.json();
        if (data && data.token) {
          const tokenExp = Math.max(
            now + TOKEN_EXPIRY_SKEW_MS,
            getTokenExpiryMs(data.token) - TOKEN_EXPIRY_SKEW_MS
          );
          sessionStorage.setItem("ff_marketplace_session", data.token);
          sessionStorage.setItem("ff_marketplace_session_exp", tokenExp);
          promise.exp = tokenExp;
          return data.token;
        }
      } catch (e) {
        console.error("Failed to fetch marketplace session", e);
      }
      window.__marketplaceSessionPromise = null;
      return null;
    })();
    // Set temporary expiration so concurrent requests don't clear it
    promise.exp = now + 10 * 1000;
    window.__marketplaceSessionPromise = promise;
  }

  return await window.__marketplaceSessionPromise;
}

export function clearMarketplaceSessionTokenCache() {
  try {
    sessionStorage.removeItem("ff_marketplace_session");
    sessionStorage.removeItem("ff_marketplace_session_exp");
  } catch (_) {
    // noop
  }

  window.__marketplaceSessionPromise = null;
}
